import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "2,400+", label: "Applications prepared" },
  { value: "26", label: "Schengen states covered" },
  { value: "48h", label: "Standard turnaround" },
  { value: "Doorstep", label: "Printed & delivered, included" },
];

export const Hero = ({ onNavigate }) => {
  const { scrollY } = useScroll();
  const yMain = useTransform(scrollY, [0, 700], [0, -70]);
  const yCard = useTransform(scrollY, [0, 700], [0, 50]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center px-6 md:px-12 pt-36 pb-14 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto w-full grid lg:grid-cols-12 gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center gap-4 mb-10"
          >
            <span className="h-px w-12 bg-slate-900" />
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500">
              Visa documentation, done properly
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-slate-900"
          >
            <MaskedLine delay={0.15}>Travel, without</MaskedLine>
            <MaskedLine delay={0.28}>
              the <em className="italic font-medium text-slate-500">paperwork</em>
            </MaskedLine>
            <MaskedLine delay={0.41}>panic.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
            className="mt-9 max-w-lg text-base text-slate-600 leading-relaxed"
          >
            Visa paperwork can be confusing, time-consuming and stressful.
            Triodocz makes the process simple. We assess your requirements,
            help complete your visa application forms and organize your
            documents for submission. Once ready, we print your complete
            application package and deliver it straight to your doorstep - so
            everything is ready when you are.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-start-file-button"
              onClick={() => onNavigate("#contact")}
              className="group flex items-center gap-3 bg-slate-900 text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-slate-700 transition-colors duration-300"
            >
              Start your file
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-see-packages-button"
              onClick={() => onNavigate("#products")}
              className="group flex items-center gap-3 text-sm font-semibold text-slate-900 border-b border-slate-900 pb-1 hover:text-slate-500 hover:border-slate-400 transition-colors duration-300"
            >
              See packages
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative h-[540px] hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            style={{ y: yMain }}
            className="absolute right-0 top-0 w-[74%] aspect-[3/4] overflow-hidden"
          >
            <img
              src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=1200&auto=format&fit=crop"
              alt="Eiffel Tower, Paris"
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.75, ease: EASE }}
            style={{ y: yCard }}
            className="absolute left-0 bottom-6 w-[48%] aspect-square overflow-hidden border-[10px] border-[#FAFAFA] shadow-[0_32px_64px_-24px_rgba(15,23,42,0.35)]"
          >
            <img
              src="https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?q=80&w=800&auto=format&fit=crop"
              alt="Santorini, Greece"
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="absolute right-4 bottom-24 bg-white/80 backdrop-blur-md border border-black/5 px-5 py-4"
          >
            <p className="font-display text-lg text-slate-900 leading-none">Schengen</p>
            <p className="text-xs tracking-[0.2em] uppercase text-slate-500 mt-1.5">26 states, one file</p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.15 }}
        className="max-w-[1400px] mx-auto w-full mt-20 pt-8 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-6"
        data-testid="hero-stats"
      >
        {STATS.map((s) => (
          <div key={s.label}>
            <p className="font-display text-2xl sm:text-3xl text-slate-900">{s.value}</p>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">{s.label}</p>
          </div>
        ))}
      </motion.div>
    </section>
  );
};
